import _ from 'underscore'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import Form from 'pages/shared/Form'
import GroupMetricFields from 'pages/shared/GroupMetricForm/GroupMetricFields'

import { convertToTextareaValue, parseIntoDescriptionsAndDetails } from 'helpers/alerts'

import { LoadingSinglePanePage } from 'styles/shared'

import { GroupMetric, GroupMetricErrors } from 'types/DeviceMetric'

import useDeviceMetrics from 'hooks/useDeviceMetrics'
import useTrends from 'hooks/useTrends'
import { useStoreContext } from 'hooks/StoreContext'

import GroupMetricModel from 'models/GroupMetric'
export interface MetricsList {
  id: string,
  tag: string | null
  title: string,
}

type Props = {
  title: string,
  saveMethod: 'create' | 'update',
  groupMetric: GroupMetric
}
interface GroupMetricValues extends Omit<GroupMetric, 'callToAction'> {
  callToAction: string;
}

const groupMetricModel = new GroupMetricModel()

const GroupMetricForm = ({ title, saveMethod, groupMetric }: Props) => {
  const navigate = useNavigate()
  useDeviceMetrics()
  useTrends()

  const { deviceMetrics, areDeviceMetricsLoaded, trends, areTrendsLoaded } = useStoreContext()

  const [formAttributes, setFormAttributes] = useState<GroupMetricValues>({
    ...groupMetric,
    callToAction: convertToTextareaValue(groupMetric.callToAction)
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedMetrics, setSelectedMetrics] = useState<Array<MetricsList>>([])

  const { draft, name, unit, description, range, callToAction } = formAttributes
  const [errors, setErrors] = useState<GroupMetricErrors>({ draft: '', name: '', callToAction: '', description: '', metrics: '', range: '', unit: '' })

  const onGroupDeviceMetricUpdateCallback = (updateAttributes) => {
    setFormAttributes({ ...formAttributes, ...updateAttributes })
  }

  const onMetricChangeCallback = (selectedMetrics) => {
    const metrics = selectedMetrics.map(({ id }) => ({ 'metricId': id }))
    setFormAttributes({ ...formAttributes, metrics })
    setSelectedMetrics(selectedMetrics)
  }

  useEffect(() => {
    if (areDeviceMetricsLoaded && groupMetric.metrics && groupMetric?.metrics.length > 0) {
      const selectedMetrics = groupMetric.metrics.map(({ metricId }) => {
        const metric = _.find(deviceMetrics, deviceMetric => deviceMetric.id === metricId)
        return { id: metricId, title: metric.name, tag: metric.draft ? 'draft' : null, color: metric.color }
      })

      setSelectedMetrics(selectedMetrics)
    }
  }, [areDeviceMetricsLoaded, groupMetric, deviceMetrics])

  const onSave = useCallback(() => {
    setIsSaving(true)

    const onSaveSuccessCallback = () => {
      navigate('/admin/groups')
    }

    const onValidationErrorCallback = (errors) => {
      setErrors(errors)

      setIsSaving(false)
    }

    const onSaveFailureCallBack = () => {
      setIsSaving(false)

      alert('Save Failed')
    }

    groupMetricModel.save({
      ...formAttributes,
      callToAction: parseIntoDescriptionsAndDetails(formAttributes.callToAction)
    }, trends, deviceMetrics, saveMethod, onSaveSuccessCallback, onSaveFailureCallBack, onValidationErrorCallback)
  }, [navigate, trends, deviceMetrics, formAttributes, saveMethod])

  const onCancel = () => {
    const parsedFormAttributes:GroupMetric = { ...formAttributes, callToAction: parseIntoDescriptionsAndDetails(formAttributes.callToAction) }
    const hasChanges = !_.isEqual(parsedFormAttributes, { ...groupMetric })

    if (!hasChanges || (hasChanges && confirm('Are you sure you want to cancel?'))) {
      navigate('/admin/groups')
    }
  }

  const onDelete = useCallback(() => {
    if (confirm('Are you sure you want to delete this Group permanently? and cannot be undone.')) {
      setIsDeleting(true)

      const onDeleteSuccessCallback = () => {
        navigate('/admin/groups')
      }

      const onDeleteFailureCallback = () => {
        setIsDeleting(false)

        alert('Delete Failed')
      }

      groupMetricModel.delete(onDeleteSuccessCallback, onDeleteFailureCallback, groupMetric.groupId)
    }
  }, [navigate, groupMetric.groupId])

  if (!areDeviceMetricsLoaded && !areTrendsLoaded) {
    return <LoadingSinglePanePage />
  }

  return (
    <S.Form
      fullScreen
      title={title}
      isDeleting={isDeleting}
      isSaving={isSaving}
      deleteButtonText={'Group'}
      displayDeleteButton={saveMethod === 'update'}
      onSaveCallback={onSave}
      onCancelCallback={onCancel}
      onDeleteCallback={onDelete}
    >
      <GroupMetricFields
        deviceMetrics={deviceMetrics}
        onMetricChangeCallback={onMetricChangeCallback}
        selectedMetrics={selectedMetrics}
        name={name}
        description={description}
        callToAction={callToAction}
        range={range}
        unit={unit}
        draft={draft}
        onChangeCallback={onGroupDeviceMetricUpdateCallback}
        errors={errors}
      />
    </S.Form>
  )
}

export default GroupMetricForm

const S = {
  Form: styled(Form)`
    max-width: 100%;
  `
}
